import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiBell, FiCheckCircle, FiTrendingUp, FiRefreshCw, FiServer, FiGlobe, FiAlertTriangle } = FiIcons;

const Dashboard = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [systemStatus, setSystemStatus] = useState({ web: 'Online', db: 'Connected', push: 'Ready' });
  const [counts, setCounts] = useState({ leads: 0, subscribers: 0 });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = () => {
    const leadsStored = localStorage.getItem('ramirez_leads');
    const subsStored = localStorage.getItem('ramirez_subscribers');
    
    // Default counts if storage is completely empty (first run), 
    // otherwise respect the true count (even if 0)
    let leadsCount = 4; // Default starting value for demo
    let subsCount = 5; // Default starting value for demo
    
    if (leadsStored) {
        try {
            const parsed = JSON.parse(leadsStored);
            if (Array.isArray(parsed)) leadsCount = parsed.length;
        } catch (e) { console.error("Error parsing leads", e); }
    }
    
    if (subsStored) {
        try {
            const parsed = JSON.parse(subsStored);
            if (Array.isArray(parsed)) subsCount = parsed.length;
        } catch (e) { console.error("Error parsing subscribers", e); }
    }
    
    setCounts({
        leads: leadsCount,
        subscribers: subsCount
    });
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Simulate data fetch
    setTimeout(() => {
      loadStats();
      setIsRefreshing(false);
    }, 800);
  };

  const handleStatusCheck = (type) => {
    setSystemStatus(prev => ({ ...prev, [type]: 'Checking...' }));
    setTimeout(() => {
      setSystemStatus(prev => ({
        ...prev,
        [type]: type === 'web' ? 'Online' : type === 'db' ? 'Connected' : 'Ready'
      }));
    }, 800);
  };

  // Mock Data for display only
  const complianceRisks = [
    { id: 1, company: 'Smith Logistics', issue: 'Inspection Due in 3 Days', severity: 'high', truckId: 'Fleet-09' },
    { id: 2, company: 'Rivera Transport', issue: 'Inspection Due in 7 Days', severity: 'medium', truckId: 'Truck-42' },
    { id: 3, company: 'J&B Hauling', issue: 'Registration Mismatch', severity: 'low', truckId: 'Unit-11' },
  ];

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Dashboard Overview</h1>
          <p className="text-gray-400">Welcome back, Oscar. Here's what's happening today.</p>
        </div>
        <button
          onClick={handleRefresh}
          className={`p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white border border-gray-700 transition-all ${isRefreshing ? 'animate-spin text-blue-500' : ''}`}
          title="Refresh Data"
        >
          <SafeIcon icon={FiRefreshCw} />
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors">
            <div className="flex justify-between items-start mb-4">
                <div className="p-3 rounded-lg bg-blue-900/20 text-blue-500">
                    <SafeIcon icon={FiUsers} className="text-xl" />
                </div>
                <span className="text-green-400 text-sm font-medium bg-green-900/20 px-2 py-1 rounded">+12%</span>
            </div>
            <h3 className="text-gray-400 text-sm font-medium">Total Leads</h3>
            <p className="text-2xl font-bold text-white mt-1">{counts.leads}</p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors">
            <div className="flex justify-between items-start mb-4">
                <div className="p-3 rounded-lg bg-yellow-900/20 text-yellow-500">
                    <SafeIcon icon={FiBell} className="text-xl" />
                </div>
                <span className="text-green-400 text-sm font-medium bg-green-900/20 px-2 py-1 rounded">+5%</span>
            </div>
            <h3 className="text-gray-400 text-sm font-medium">Active Reminders</h3>
            <p className="text-2xl font-bold text-white mt-1">{counts.subscribers}</p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors">
            <div className="flex justify-between items-start mb-4">
                <div className="p-3 rounded-lg bg-green-900/20 text-green-500">
                    <SafeIcon icon={FiCheckCircle} className="text-xl" />
                </div>
                <span className="text-green-400 text-sm font-medium bg-green-900/20 px-2 py-1 rounded">+8%</span>
            </div>
            <h3 className="text-gray-400 text-sm font-medium">Completed Checks</h3>
            <p className="text-2xl font-bold text-white mt-1">42</p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors">
            <div className="flex justify-between items-start mb-4">
                <div className="p-3 rounded-lg bg-purple-900/20 text-purple-500">
                    <SafeIcon icon={FiTrendingUp} className="text-xl" />
                </div>
                <span className="text-green-400 text-sm font-medium bg-green-900/20 px-2 py-1 rounded">+15%</span>
            </div>
            <h3 className="text-gray-400 text-sm font-medium">Site Visits</h3>
            <p className="text-2xl font-bold text-white mt-1">1.2k</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Compliance Watchlist */}
        <div className="lg:col-span-2 bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <SafeIcon icon={FiAlertTriangle} className="text-yellow-500" />
              Compliance Watchlist
            </h2>
            <span className="text-xs text-gray-500">Automated Alerts</span>
          </div>
          <div className="space-y-4">
            {complianceRisks.map((risk) => (
              <div key={risk.id} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg border border-gray-700 hover:border-gray-500 transition-colors">
                <div className="flex items-center gap-4">
                  <div className={`w-2 h-12 rounded-full ${risk.severity === 'high' ? 'bg-red-500' : risk.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'}`}></div>
                  <div>
                    <h4 className="font-bold text-white">{risk.company}</h4>
                    <p className="text-sm text-gray-400">{risk.truckId} • {risk.issue}</p>
                  </div>
                </div>
                <button className="px-3 py-1.5 text-xs font-medium bg-gray-700 text-white rounded hover:bg-blue-600 transition-colors">
                  Contact
                </button>
              </div>
            ))}
            <div className="pt-2 text-center">
              <button className="text-blue-400 text-sm hover:underline">View All Risks</button>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg">
          <h2 className="text-lg font-bold text-white mb-4">System Status</h2>
          <div className="space-y-4">
            <div onClick={() => handleStatusCheck('web')} className="flex justify-between items-center p-4 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-800 rounded-full">
                  <SafeIcon icon={FiGlobe} className={`text-sm ${systemStatus.web === 'Online' ? 'text-green-500' : 'text-yellow-500'}`} />
                </div>
                <span className="text-gray-200">Website Status</span>
              </div>
              <span className={`font-medium ${systemStatus.web === 'Online' ? 'text-green-400' : 'text-yellow-400'}`}>
                {systemStatus.web}
              </span>
            </div>
            <div onClick={() => handleStatusCheck('db')} className="flex justify-between items-center p-4 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-800 rounded-full">
                  <SafeIcon icon={FiServer} className={`text-sm ${systemStatus.db === 'Connected' ? 'text-green-500' : 'text-yellow-500'}`} />
                </div>
                <span className="text-gray-200">Database Connection</span>
              </div>
              <span className={`font-medium ${systemStatus.db === 'Connected' ? 'text-green-400' : 'text-yellow-400'}`}>
                {systemStatus.db}
              </span>
            </div>
            <div onClick={() => handleStatusCheck('push')} className="flex justify-between items-center p-4 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-800 rounded-full">
                  <SafeIcon icon={FiBell} className={`text-sm ${systemStatus.push === 'Ready' ? 'text-yellow-500' : 'text-blue-500'}`} />
                </div>
                <span className="text-gray-200">Push Notifications</span>
              </div>
              <span className={`font-medium ${systemStatus.push === 'Ready' ? 'text-yellow-400' : 'text-blue-400'}`}>
                {systemStatus.push}
              </span>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;